import { useEffect, useCallback } from 'react';
import { useToast } from '@chakra-ui/react';
import { useDebugStore } from '../../../../stores/debugStore';
import { useWebSocketChatContext } from '../../../../contexts/WebSocketChatContext';

export const useWebSocketDebug = () => {
  const toast = useToast();
  const wsContext = useWebSocketChatContext();
  
  const {
    addDetectedConcept,
    setSystemPrompt,
    setTeachingStrategy,
    updateUserSkill,
    addWsEvent,
    addEvent,
    showNotifications,
  } = useDebugStore();

  // Handle concept detection events
  const handleConceptDetection = useCallback((data: any) => {
    console.log('[Debug] Concept detected:', data);
    
    // Add to detected concepts
    if (data.concepts && Array.isArray(data.concepts)) {
      data.concepts.forEach((concept: any) => {
        addDetectedConcept({
          skill_code: concept.skill_code,
          skill_name: concept.skill_name,
          confidence: concept.confidence,
          performance: concept.performance,
          timestamp: new Date().toISOString(),
          notes: concept.notes,
        });
      });
      
      if (showNotifications) {
        toast({
          title: 'Concepts detected',
          description: `${data.concepts.length} concepts identified`,
          status: 'info',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    }
    
    // Log the event
    addWsEvent({
      type: 'concept_detection',
      data: data,
    });
    addEvent({ type: 'concept_detection', data });
  }, [addDetectedConcept, addWsEvent, showNotifications, toast]);

  // Handle skill proficiency updates
  const handleSkillUpdate = useCallback((data: any) => {
    console.log('[Debug] Skill updated:', data);
    
    if (data.skill_id) {
      updateUserSkill(data.skill_id, {
        proficiency: data.proficiency,
        confidence: data.confidence,
      });
      
      if (showNotifications) {
        toast({
          title: 'Skill updated',
          description: `${data.skill_name || data.skill_code}: ${Math.round(data.proficiency * 100)}%`,
          status: 'success',
          duration: 2000,
          isClosable: true,
          position: 'bottom-right',
        });
      }
    }
    
    // Log the event
    addWsEvent({
      type: 'skill_update',
      data: data,
    });
    addEvent({ type: 'skill_update', data });
  }, [updateUserSkill, addWsEvent, showNotifications, toast]);

  // Handle system prompt generation
  const handlePromptGeneration = useCallback((data: any) => {
    console.log('[Debug] System prompt generated:', data);
    
    if (data.prompt) {
      setSystemPrompt(data.prompt);
    }
    
    if (data.strategy) {
      setTeachingStrategy({
        current: data.strategy,
        reason: data.strategy_reason,
        confidence: data.strategy_confidence,
      });
    }
    
    // Log the event
    addWsEvent({
      type: 'prompt_generation',
      data: data,
    });
    addEvent({ type: 'prompt_generation', data });
  }, [setSystemPrompt, setTeachingStrategy, addWsEvent]);

  // Handle generic WebSocket events
  const handleGenericEvent = useCallback((eventType: string, data: any) => {
    console.log(`[Debug] WS Event - ${eventType}:`, data);
    
    // Log all events
    addWsEvent({
      type: eventType,
      data: data,
    });
    addEvent({ type: eventType, data });
  }, [addWsEvent]);

  // Set up WebSocket event listeners
  useEffect(() => {
    if (!wsContext) {
      console.log('[Debug] WebSocket context not available');
      return;
    }

    // Create a debug message handler
    const debugHandler = (message: any) => {
      console.log('[Debug] WebSocket message:', message);
      
      // Handle different message types
      switch (message.type) {
        case 'concept_detection':
        case 'concepts_detected':
          handleConceptDetection(message.data || message);
          break;
          
        case 'skill_update':
        case 'proficiency_update':
          handleSkillUpdate(message.data || message);
          break;
          
        case 'prompt_generated':
        case 'system_prompt':
          handlePromptGeneration(message.data || message);
          break;
          
        case 'teaching_strategy':
          if (message.strategy) {
            setTeachingStrategy({
              current: message.strategy,
              reason: message.reason,
              confidence: message.confidence,
            });
          }
          handleGenericEvent('teaching_strategy', message);
          break;
          
        default:
          // Log all other events
          handleGenericEvent(message.type || 'unknown', message);
      }
    };

    // If the context has a way to subscribe to messages, use it
    // Otherwise, we'll need to patch into the WebSocket instance
    // This depends on how your WebSocket context is implemented
    
    // For now, let's log that we're ready to receive events
    console.log('[Debug] WebSocket debug listeners initialized');
    
    // Clean up
    return () => {
      console.log('[Debug] WebSocket debug listeners cleaned up');
    };
  }, [
    wsContext,
    handleConceptDetection,
    handleSkillUpdate,
    handlePromptGeneration,
    handleGenericEvent,
    setTeachingStrategy,
  ]);

  // Expose methods for manual testing
  const simulateConceptDetection = useCallback(() => {
    handleConceptDetection({
      concepts: [
        {
          skill_code: 'test.skill.example',
          skill_name: 'Example Skill',
          confidence: 0.85,
          performance: 'understood',
          notes: 'Simulated concept detection for testing',
        },
      ],
    });
  }, [handleConceptDetection]);

  const simulateSkillUpdate = useCallback(() => {
    handleSkillUpdate({
      skill_id: 'test-skill-id',
      skill_code: 'test.skill.example',
      skill_name: 'Example Skill',
      proficiency: Math.random(),
      confidence: Math.random(),
    });
  }, [handleSkillUpdate]);

  const simulatePromptGeneration = useCallback(() => {
    handlePromptGeneration({
      prompt: 'This is a simulated system prompt for testing the debug panel.',
      strategy: 'adaptive',
      strategy_reason: 'Default testing strategy',
      strategy_confidence: 0.9,
    });
  }, [handlePromptGeneration]);

  return {
    simulateConceptDetection,
    simulateSkillUpdate,
    simulatePromptGeneration,
  };
};
